import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { ProjectParam, SlideData, slideTypeShortcodes } from 'app/models/slideData.model';
import { ProjectSlidesService } from 'app/services/project-slides.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { lessonImageContentUrlCandidates } from '@app/utils/lesson-image-url';
import { lessonSlideService } from '@app/services/lesson-slide.service';
import { alertSeedingHttpError, alertSeedingResult } from '@app/utils/seed-issues-alert';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';
import { LanguageRow, LanguageService } from '@app/services/language.service';
import { ProjectCurrentLanguageService } from '@app/services/project-current-language.service';

@Component({
  selector: 'app-project-slides',
  templateUrl: './project-slides.component.html',
  styleUrl: './project-slides.component.scss'
})
export class ProjectSlidesComponent implements OnInit {
  projectCode!: string;
  slides: SlideData[] = [];
  allSelected = false;
  projectParams: ProjectParam[] = [];
  layoutOptions: string[] = [];
  slideTypeShortcodes: slideTypeShortcodes[] = [];
  layoutoptionstr: string[] = [];
  newSlide: Partial<SlideData> = { slideTitle: '', slideLayoutSC: '', slideRemarks: '', slideType: '', SlideSrNo: 1 };
  showAddForm = false;
 loading_chk = false;
 loading_dep = false;
 loading_exp = false;
  loadingStep1 = false;
  loadingStep2 = false;
  loadingStep3 = false;
  replaceExistingFromLesson = false;

  // Project-level common assets (for packaging / add-in)
  uploadingCommon = false;
  commonAssetType: 'IMAGE' | 'AUDIO' = 'IMAGE';
  commonAssetFile: File | null = null;
  uploadingAssetsZip = false;
  assetsZipFileName = '';
  private pendingAssetsZipBase64 = '';
  private pendingAssetsZipName = '';

  // Per-slide actions
  seedingSlideId: number | null = null;
  clearSeedingSlideId: number | null = null;
  savingSlideId: number | null = null;

  // Preview (approx, like SME chat)
  previewSlideId: number | null = null;
  previewLoading = false;
  previewTitle = '';
  previewImageUrl: string | null = null;
  /** Same order as lesson-image-url (per-slide dir, legacy slides/, legacy images/). */
  previewImageCandidates: string[] = [];
  private previewImageCandidateIdx = 0;
  previewTextLines: Array<{ text: string; bullet: boolean; indent: number }> = [];

  // Linked lesson context (needed to preview lesson slides)
  linkedLessonUserId: number | null = null;
  linkedLessonId: number | null = null;

  // Keep scroll/focus on last-touched slide
  private lastTouchedSlideId: number | null = null;

  languages: LanguageRow[] = [];

  constructor(
    private route: ActivatedRoute,
    private slidesService: ProjectSlidesService,
    private router: Router,
    private lessonSlideSvc: lessonSlideService,
    private languageSvc: LanguageService,
    private projectCurrentLangSvc: ProjectCurrentLanguageService,
  ) { }

  ngOnInit(): void {
    //debugger;

    this.route.paramMap.subscribe(params => {
      this.projectCode = params.get('shortcode') || '';
      console.log("initi with");
      console.log(this.projectCode);

      if (this.projectCode) {

        this.loadCustomLayouts();
        this.loadslideTypes();
        this.loadSlides();
        this.loadLinkedLesson();
        this.loadLanguages();

      }
    });
  }

  private lastTouchedStorageKey(): string {
    return `creator.projectSlides.lastTouched.${this.projectCode}`;
  }

  private rememberLastTouched(slideId: number | null | undefined) {
    const id = slideId != null ? Number(slideId) : null;
    if (!id || !Number.isFinite(id)) return;
    this.lastTouchedSlideId = id;
    try {
      localStorage.setItem(this.lastTouchedStorageKey(), String(id));
    } catch { /* ignore */ }
  }

  private restoreLastTouched(): number | null {
    try {
      const raw = localStorage.getItem(this.lastTouchedStorageKey());
      const id = raw != null ? Number(raw) : null;
      return id && Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  }

  private focusSlideRow(slideId: number) {
    const el = document.getElementById(`ps-row-${slideId}`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { el.focus({ preventScroll: true } as any); } catch { try { el.focus(); } catch { } }
  }

  onCommonAssetFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const f = input?.files?.[0] || null;
    this.commonAssetFile = f;
  }

  uploadCommonAsset(): void {
    if (!this.commonAssetFile) {
      alert('Pick a file first.');
      return;
    }
    const file = this.commonAssetFile;
    const reader = new FileReader();
    this.uploadingCommon = true;
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.slidesService.uploadProjectAsset(this.projectCode, this.commonAssetType, file.name, dataUrl).subscribe({
        next: (res) => {
          this.uploadingCommon = false;
          this.commonAssetFile = null;
          alert(res?.message || 'Uploaded.');
        },
        error: (err) => {
          this.uploadingCommon = false;
          console.error('Upload common asset failed', err);
          alert(extractApiErrorMessage(err, 'Upload failed'));
        }
      });
    };
    reader.onerror = () => {
      this.uploadingCommon = false;
      alert('Failed to read file.');
    };
    reader.readAsDataURL(file);
  }

  onAssetsZipSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    input.value = '';
    if (!file) return;
    this.assetsZipFileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      this.pendingAssetsZipBase64 = b64;
      this.pendingAssetsZipName = file.name;
      this.sendAssetsZip(false);
    };
    reader.onerror = () => alert('Failed to read ZIP.');
    reader.readAsDataURL(file);
  }

  private sendAssetsZip(overwrite: boolean): void {
    if (!this.pendingAssetsZipBase64 || !this.projectCode) return;
    this.uploadingAssetsZip = true;
    this.slidesService
      .uploadProjectAssetsZip(
        this.projectCode,
        this.pendingAssetsZipName,
        this.pendingAssetsZipBase64,
        overwrite,
      )
      .subscribe({
        next: (res) => {
          this.uploadingAssetsZip = false;
          this.pendingAssetsZipBase64 = '';
          const n = res?.totalWritten ?? 0;
          const ow = (res?.overwritten || []).length;
          alert(
            ow
              ? `ZIP extracted: ${n} file(s), ${ow} overwritten under projects/${this.projectCode}/`
              : `ZIP extracted: ${n} file(s) under projects/${this.projectCode}/`,
          );
        },
        error: (err) => {
          this.uploadingAssetsZip = false;
          const body = err?.error || {};
          if (body?.code === 'NEEDS_OVERWRITE' || err?.status === 409) {
            const count = body.conflictCount || (body.conflicts || []).length;
            const ok = confirm(
              `${count} file(s) already exist under projects/${this.projectCode}/.\n\nOverwrite existing files?`,
            );
            if (ok) this.sendAssetsZip(true);
            else alert('ZIP upload cancelled — existing files kept.');
            return;
          }
          if (body?.code === 'INVALID_ZIP_LAYOUT') {
            const extra = (body.invalidPaths || []).slice(0, 5).join('\n');
            alert((body.error || body.message || 'Invalid ZIP layout') + (extra ? `\n${extra}` : ''));
            return;
          }
          console.error('Upload assets ZIP failed', err);
          alert(extractApiErrorMessage(err, 'ZIP upload failed'));
        },
      });
  }

  loadLinkedLesson(): void {
    this.slidesService.getLinkedLesson(this.projectCode).subscribe({
      next: (res) => {
        if (res && res.linked) {
          this.linkedLessonUserId = Number(res.lessonUserId);
          this.linkedLessonId = Number(res.lessonId);
        } else {
          this.linkedLessonUserId = null;
          this.linkedLessonId = null;
        }
      },
      error: () => {
        this.linkedLessonUserId = null;
        this.linkedLessonId = null;
      },
    });
  }

  private parseLessonSlideIdFromRemarks(remarks?: string): string | null {
    const r = String(remarks || '');
    const m = r.match(/lessonSlideId:([^\s|]+)/);
    return m ? m[1].trim() : null;
  }

  /** Section-break slides use pickLessonSlideId: (first TOC slide in section) for lesson image pick only — not for preview. */
  private parsePickLessonSlideIdFromRemarks(remarks?: string): string | null {
    const r = String(remarks || '');
    const m = r.match(/pickLessonSlideId:([^\s|]+)/i);
    return m ? m[1].trim() : null;
  }

  private parseLessonSlideIdForImagePickFromRemarks(remarks?: string): string | null {
    return this.parseLessonSlideIdFromRemarks(remarks) || this.parsePickLessonSlideIdFromRemarks(remarks);
  }

  /** TOC section header rows (e.g. remarks Section2|pickLessonSlideId:…). */
  isSectionBreakSlide(slide: SlideData | null | undefined): boolean {
    if (!slide) return false;
    const r = String(slide.slideRemarks || '').trim();
    if (/^Section\d+/i.test(r)) return true;
    if (/pickLessonSlideId:/i.test(r) && !/lessonSlideId:/i.test(r)) return true;
    const typ = String(slide.slideType || '').trim().toUpperCase();
    return typ.includes('TRANSITION') || typ.includes('SECTIONBREAK') || typ.includes('SECTION_BREAK');
  }

  /** Remarks column: show Section2 only — hide internal pickLessonSlideId metadata. */
  displaySlideRemarks(slide: SlideData | null | undefined): string {
    const r = String(slide?.slideRemarks || '').trim();
    if (!r) return '';
    if (/^Section\d+/i.test(r)) {
      const m = r.match(/^(Section\d+)/i);
      return m ? m[1] : r.split('|')[0].trim();
    }
    return r;
  }

  private normalizedElementType(el: any): string {
    return String(el?.elementType ?? '').trim().toUpperCase();
  }

  onPreviewImageError(ev: Event): void {
    const list = this.previewImageCandidates;
    const img = ev.target as HTMLImageElement;
    this.previewImageCandidateIdx += 1;
    if (this.previewImageCandidateIdx < list.length) {
      img.src = list[this.previewImageCandidateIdx];
    } else {
      this.previewImageUrl = null;
    }
  }

  /** Indent for preview lines (matches SME chat spacing). */
  previewIndentPx(indent: number): number {
    const safe = Math.max(0, Math.min(Number(indent ?? 0), 6));
    return safe * 12;
  }

  loadslideTypes(): void {

    this.slidesService.getslideTypeSC(this.projectCode).subscribe({
      next: slideTypeShortcodes => {
        console.log(slideTypeShortcodes);
        if (slideTypeShortcodes && slideTypeShortcodes.length > 0) {
          this.slideTypeShortcodes = slideTypeShortcodes;

        }
      },
      error: err => {
        console.error('Failed to load slides', err);
        this.projectParams = [];
      }
    });
  }

  loadLanguages(): void {
    this.languageSvc.getLanguages().subscribe({
      next: (rows: LanguageRow[]) => {
        this.languages = Array.isArray(rows) ? rows : [];
      },
      error: () => {
        this.languages = [];
      },
    });
  }

  get projectCurrentLanguage(): string {
    return this.projectCurrentLangSvc.get(this.projectCode);
  }

  set projectCurrentLanguage(code: string) {
    this.projectCurrentLangSvc.set(this.projectCode, code);
  }

  loadCustomLayouts(): void {
    //debugger;
    this.slidesService.getProjectParameters(this.projectCode).subscribe({
      next: pparams => {
        console.log(pparams);
        if (pparams && pparams.length > 0) {
          this.projectParams = pparams; // <-- only use first result
          let layoutopstr = this.projectParams[0].projectLayoutsSC;
          console.log(layoutopstr);
          this.layoutOptions = JSON.parse(layoutopstr);
          console.log(this.layoutOptions);
        }
      },
      error: err => {
        console.error('Failed to load slides', err);
        this.projectParams = [];
      }
    });

  }


  loadSlides(): void {
    this.slidesService.getSlidesByProject(this.projectCode).subscribe({
      next: slides => {
        this.slides = [...slides].sort((a, b) => a.SlideSrNo - b.SlideSrNo);
        this.allSelected = this.slides.every(s => s.selected);
        const toFocus = this.lastTouchedSlideId ?? this.restoreLastTouched();
        if (toFocus != null) setTimeout(() => this.focusSlideRow(toFocus), 0);
      },
      error: err => {
        console.error('Failed to load slides', err);
        this.slides = [];
      }
    });
  }

  // Selection
  toggleSelect(slide: SlideData) {
    slide.selected = !slide.selected;
    this.allSelected = this.slides.every(s => s.selected);
  }
  selectAllToggle() {
    this.allSelected = !this.allSelected;
    this.slides.forEach(s => s.selected = this.allSelected);
  }

  // Ordering
  private persistOrder() {
    // Only persist order for slides that actually exist in DB.
    // (During add, we may temporarily have ProjectSlideId=0 in memory.)
    const existing = this.slides.filter((s) => Number(s.ProjectSlideId) > 0);
    existing.forEach((s, i) => (s.SlideSrNo = i + 1));
    this.slidesService.updateOrder(this.projectCode, existing).subscribe({
      error: err => console.error('Failed to update order', err)
    });

  }
  moveUp(index: number) {
    if (index <= 0) return;
    [this.slides[index - 1], this.slides[index]] = [this.slides[index], this.slides[index - 1]];
    this.rememberLastTouched(this.slides[index - 1]?.ProjectSlideId);
    this.persistOrder();
    setTimeout(() => this.focusSlideRow(this.slides[index - 1].ProjectSlideId), 0);
  }
  moveDown(index: number) {
    if (index >= this.slides.length - 1) return;
    [this.slides[index], this.slides[index + 1]] = [this.slides[index + 1], this.slides[index]];
    this.rememberLastTouched(this.slides[index + 1]?.ProjectSlideId);
    this.persistOrder();
    setTimeout(() => this.focusSlideRow(this.slides[index + 1].ProjectSlideId), 0);
  }

  // Add
  openAddSlide() {
    this.newSlide = {
      ProjectSlideId: 0,
      slideTitle: '',
      slideLayoutSC: '',
      slideRemarks: '',
      slideType: '',
      SlideSrNo: 1,
    };
    this.showAddForm = true;
  }
  saveNewSlide() {
    if (!this.newSlide.slideTitle || !this.newSlide.slideLayoutSC) {
      alert('Title and Layout Short Code are required');
      return;
    }
    // Don't optimistically inject a ProjectSlideId=0 row then reorder;
    // that can shift existing SlideSrNo and create gaps (e.g. list starts at 2).
    this.slidesService.addSlide(this.projectCode, this.newSlide).subscribe({
      next: (res: any) => {
        // After insert, reload and normalize SrNo order (important if inserted in middle).
        const newId = Number(res?.projectSlideId ?? res?.data?.projectSlideId ?? 0) || null;
        if (newId) this.rememberLastTouched(newId);
        this.loadSlides();
      },
      error: err => console.error('Failed to add slide', err)
    });
    this.showAddForm = false;
  }

  // Delete
  deleteSlide(slide: SlideData) {

    if (!confirm(`Delete slide "${slide.slideTitle}"?`)) return;
    console.log(slide);
    this.slidesService.deleteSlide(this.projectCode, slide.ProjectSlideId).subscribe({
      next: () => {
        // after deletion, keep focus near the deleted row
        const idx = this.slides.findIndex(s => s.ProjectSlideId === slide.ProjectSlideId);
        const neighbor = idx > 0 ? this.slides[idx - 1] : this.slides[idx + 1];
        if (neighbor) this.rememberLastTouched(neighbor.ProjectSlideId);
        this.loadSlides();
      },
      error: err => console.error('Failed to delete slide', err)
    });
  }
  deleteSelected() {
    const selectedIds = this.slides.filter(s => s.selected).map(s => s.ProjectSlideId);
    if (!selectedIds.length) {
      alert('No slides selected');
      return;
    }
    if (!confirm(`Delete ${selectedIds.length} selected slide(s)?`)) return;
    this.slidesService.deleteSlides(this.projectCode, selectedIds).subscribe({
      next: () => this.loadSlides(),
      error: err => console.error('Failed to delete selected slides', err)
    });
  }

  // Actions
  checkSlides() {
    const selectedIds = this.slides.filter(s => s.selected).map(s => s.ProjectSlideId);
    if (!selectedIds.length) {
      alert('No slides selected');
      return;
    }
    this.loading_chk = true;

    this.slidesService.checkSlides(this.projectCode, selectedIds).subscribe({
      next: (blob: Blob) => {
        this.loading_chk = false;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `project_${this.projectCode}_errorlist.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.loading_chk = false;
        console.error('Check slides failed', err);
        alert(extractApiErrorMessage(err, 'Could not generate slide check report. Processing may not have completed.'));
      }
    });
  }
  
  createDependencyList() {
    const selectedIds = this.slides.filter(s => s.selected).map(s => s.ProjectSlideId);
    if (!selectedIds.length) {
      alert('No slides selected');
      return;
    }
    this.loading_dep = true;

    this.slidesService.dependencyList(this.projectCode, selectedIds).subscribe({
      next: (blob: Blob) => {
        this.loading_dep = false;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `project_${this.projectCode}_dependList.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.loading_dep = false;
        console.error('Dependency list failed', err);
        alert(extractApiErrorMessage(err, 'Could not create dependency list. Processing may not have completed.'));
      }
    });
  }
  exportForPPT() {
    const selectedIds = this.slides.filter(s => s.selected).map(s => s.ProjectSlideId);
    if (!selectedIds.length) {
      alert('No slides selected');
      return;
    }
    this.loading_exp = true;

    this.slidesService.exportForPPT(this.projectCode, selectedIds).subscribe({
      next: (blob: Blob) => {
        this.loading_exp = false;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `project_${this.projectCode}_PPTGEN.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.loading_exp = false;
        console.error('Export failed', err);
        alert(extractApiErrorMessage(err, 'Error exporting slides. Processing may not have completed — try again.'));
      }
    });  }
  private buildStepMessage(data: any): string {
    if (!data || typeof data !== 'object') return 'Done.';
    const msg = data.message ?? '';
    const extra: string[] = [];
    if (data.skipped) extra.push('(skipped)');
    if (data.slidesCreated != null) extra.push(`slides created: ${data.slidesCreated}`);
    if (data.slideCount != null) extra.push(`existing: ${data.slideCount}`);
    if (data.slidesSeeded != null) extra.push(`slides seeded: ${data.slidesSeeded}`);
    if (data.slidesTouched != null) extra.push(`slides touched: ${data.slidesTouched}`);
    if (data.linesUpdated != null) extra.push(`lines updated: ${data.linesUpdated}`);
    return [msg, extra.join(' ')].filter(Boolean).join(' — ') || 'Done.';
  }

  runBuildStep1(): void {
    if (this.replaceExistingFromLesson) {
      if (!confirm('Replace existing project slides and lines? This cannot be undone.')) return;
    }
    this.loadingStep1 = true;
    this.slidesService.buildSlidesStep1FromLesson(this.projectCode, this.replaceExistingFromLesson).subscribe({
      next: (data) => {
        this.loadingStep1 = false;
        alertSeedingResult(data, this.buildStepMessage(data));
        this.loadSlides();
      },
      error: (err) => {
        this.loadingStep1 = false;
        console.error('Step 1 failed', err);
        alertSeedingHttpError(err);
      }
    });
  }

  runBuildStep2(): void {
    let msg = 'Seed / rematerialize slide lines for all slides (Step 2)?';
    if (this.linkedLessonUserId != null && this.linkedLessonId != null) {
      msg =
        'Step 2 rematerializes lines from the linked lesson for every slide.\n\n' +
        'This discards Creator edits (reorder, added groups, value changes) on existing lines. Prefer per-slide "Apply group layout" if you only need to refresh object-group positions.\n\nContinue?';
    }
    if (!confirm(msg)) return;
    this.loadingStep2 = true;
    this.slidesService.buildSlidesStep2SeedLines(this.projectCode).subscribe({
      next: (data) => {
        this.loadingStep2 = false;
        alertSeedingResult(data, this.buildStepMessage(data));
        this.loadSlides();
      },
      error: (err) => {
        this.loadingStep2 = false;
        console.error('Step 2 failed', err);
        alertSeedingHttpError(err);
      }
    });
  }

  runBuildStep3(): void {
    this.loadingStep3 = true;
    this.slidesService.buildSlidesStep3MapLessonContent(this.projectCode).subscribe({
      next: (data) => {
        this.loadingStep3 = false;
        alertSeedingResult(data, this.buildStepMessage(data));
        this.loadSlides();
      },
      error: (err) => {
        this.loadingStep3 = false;
        console.error('Step 3 failed', err);
        alertSeedingHttpError(err);
      }
    });
  }

  editSlide(slide: SlideData) {
    this.rememberLastTouched(slide.ProjectSlideId);
    console.log('Edit slide', slide);
    
    console.log(slide);
    let shortcode = slide.ProjectSlideId;
    console.log(shortcode);
     let projectId = this.projectCode;
      let slideId = slide.ProjectSlideId;
    
    this.router.navigateByUrl(`/creator/slidelines/projects/${projectId}/slides/${slideId}`);
  }

  saveSlideMeta(slide: SlideData) {
    this.rememberLastTouched(slide.ProjectSlideId);
    this.savingSlideId = slide.ProjectSlideId;
    this.slidesService.saveSlide(this.projectCode, slide).subscribe({
      next: (data) => {
        this.savingSlideId = null;
        if (data?.seedIssues?.length) {
          alertSeedingResult(data, 'Slide saved.');
        }
      },
      error: (err) => {
        this.savingSlideId = null;
        console.error('Failed to save slide', err);
        alertSeedingHttpError(err);
      },
    });
  }

  seedLinesForSlide(slide: SlideData) {
    const st = String(slide.slideType || '').trim();
    if (!st) {
      alert('Pick a slide type first.');
      return;
    }
    this.rememberLastTouched(slide.ProjectSlideId);
    this.seedingSlideId = slide.ProjectSlideId;
    this.slidesService.seedLinesForOneSlide(this.projectCode, slide.ProjectSlideId, st, false).subscribe({
      next: (data) => {
        this.seedingSlideId = null;
        // Disable button immediately after success (backend also reports lineCount on reload).
        const inserted = Number(data?.linesInserted ?? 0);
        const existing = Number(data?.existingLines ?? 0);
        if (inserted > 0) slide.lineCount = inserted;
        else if (existing > 0) slide.lineCount = existing;
        else slide.lineCount = Math.max(1, Number(slide.lineCount ?? 0));
        const skipped = data?.seeded === false && Number(data?.existingLines ?? 0) > 0;
        alertSeedingResult(
          data,
          skipped
            ? (data?.message ||
                'Lines already exist. Use "Clear & re-seed" or run Step 2 to pick up object-group / slide-type changes.')
            : (data?.message || 'Done.')
        );
      },
      error: (err) => {
        this.seedingSlideId = null;
        console.error('Seed lines failed', err);
        alertSeedingHttpError(err);
      },
    });
  }

  /** Remove lines and slide-related audio, then seed again from the selected slide type (images kept). */
  clearAndSeedLinesForSlide(slide: SlideData) {
    const st = String(slide.slideType || '').trim();
    if (!st) {
      alert('Pick a slide type first.');
      return;
    }
    if ((slide.lineCount || 0) <= 0) {
      this.seedLinesForSlide(slide);
      return;
    }
    let msg =
      'Remove all lines for this slide, delete related audio files (MP3/WAV/etc. for this slide), and re-seed from the slide type? Images are not removed.';
    if (this.linkedLessonUserId != null && this.linkedLessonId != null) {
      msg +=
        '\n\nThis project is linked to a lesson. Re-seed rebuilds lines and discards Creator edits on this slide (reorder, added groups, values). Prefer "Apply group layout" on the slide lines screen if you only need positions refreshed.';
    }
    if (!confirm(msg)) {
      return;
    }
    this.rememberLastTouched(slide.ProjectSlideId);
    this.clearSeedingSlideId = slide.ProjectSlideId;
    this.slidesService.seedLinesForOneSlide(this.projectCode, slide.ProjectSlideId, st, true).subscribe({
      next: (data) => {
        this.clearSeedingSlideId = null;
        alertSeedingResult(data, data?.message || 'Done.');
        void this.loadSlides();
      },
      error: (err) => {
        this.clearSeedingSlideId = null;
        console.error('Clear and seed failed', err);
        alertSeedingHttpError(err);
      },
    });
  }

  togglePreview(slide: SlideData) {
    this.rememberLastTouched(slide.ProjectSlideId);
    if (this.isSectionBreakSlide(slide)) {
      this.previewSlideId = null;
      this.previewImageUrl = null;
      this.previewImageCandidates = [];
      this.previewImageCandidateIdx = 0;
      this.previewTextLines = [];
      this.previewTitle = '';
      return;
    }
    if (this.previewSlideId === slide.ProjectSlideId) {
      this.previewSlideId = null;
      this.previewImageUrl = null;
      this.previewImageCandidates = [];
      this.previewImageCandidateIdx = 0;
      this.previewTextLines = [];
      this.previewTitle = '';
      return;
    }
    this.previewSlideId = slide.ProjectSlideId;
    this.previewLoading = true;
    this.previewTitle = slide.slideTitle || `Slide ${slide.ProjectSlideId}`;
    this.previewImageUrl = null;
    this.previewImageCandidates = [];
    this.previewImageCandidateIdx = 0;
    this.previewTextLines = [];

    const lessonSlideId = this.parseLessonSlideIdForImagePickFromRemarks(slide.slideRemarks);
    if (!lessonSlideId || this.linkedLessonUserId == null || this.linkedLessonId == null) {
      this.previewLoading = false;
      this.previewTextLines = [
        {
          text: 'No linked lesson slide for images (need lessonSlideId:… in remarks, or pickLessonSlideId:… after rebuild from lesson TOC).',
          bullet: false,
          indent: 0,
        },
      ];
      return;
    }

    // Preview from lesson slide content (same intent as SME chat preview)
    this.lessonSlideSvc.getLessonSlide(this.linkedLessonUserId, this.linkedLessonId, lessonSlideId, slide.slideTitle || '').subscribe({
      next: (s) => {
        this.previewLoading = false;
        const els = Array.isArray(s?.elements) ? s.elements : [];
        const firstImg = els.find((e: any) => this.normalizedElementType(e) === 'IMAGE' && String(e?.content || '').trim());
        this.previewImageCandidates =
          firstImg && this.linkedLessonUserId != null && this.linkedLessonId != null
            ? lessonImageContentUrlCandidates(
                this.linkedLessonUserId,
                this.linkedLessonId,
                lessonSlideId,
                firstImg.content
              )
            : [];
        this.previewImageCandidateIdx = 0;
        this.previewImageUrl = this.previewImageCandidates[0] ?? null;
        this.previewTextLines = els
          .filter((e: any) => this.normalizedElementType(e) === 'TEXT')
          .map((e: any) => ({
            text: String(e?.content || ''),
            bullet: String(e?.bulletFlag || 'N').toUpperCase() === 'Y',
            indent: Number(e?.indentLevel ?? 0),
          }))
          .filter((x: any) => x.text.trim().length > 0);
      },
      error: () => {
        this.previewLoading = false;
        this.previewTextLines = [{ text: 'Failed to load lesson slide preview.', bullet: false, indent: 0 }];
        this.previewImageUrl = null;
        this.previewImageCandidates = [];
        this.previewImageCandidateIdx = 0;
      },
    });
  }
}
